import {
  CanvasHTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useEvent } from 'react-use'
import gsap from 'gsap'
import { SimplexNoise } from 'three-stdlib'

import { isCorporate } from '@/constants'

import { cn } from '@/lib/utils'
import { FluidProps } from '@/types'

export type FluidSpacerProps = CanvasHTMLAttributes<HTMLCanvasElement> &
  Partial<FluidProps> & {
    variant?: 'top' | 'bottom'
    backgroundColor?: string
  }

const FluidSpacer = ({
  segments = 40,
  amplitude = 20,
  period = 0.0009,
  speed = 0.005,
  variant = 'top',
  backgroundColor,
  className,
  ...props
}: FluidSpacerProps) => {
  const canvas = useRef<HTMLCanvasElement>(null!)
  const context = useRef<CanvasRenderingContext2D | null>(null)
  const [perlin] = useState(() => new SimplexNoise())
  const variation = useRef(0)

  const tick = useCallback(() => {
    if (!context.current) return

    const ctx = context.current
    const width = canvas.current.width
    const height = canvas.current.height
    const segment = width / segments

    ctx.fillStyle = backgroundColor || '#004c59'
    ctx.clearRect(0, 0, width, height)
    ctx.beginPath()
    ctx.moveTo(0, height / 2)
    for (let x = 0, u = 0; x <= segments; x++, u += segment) {
      const point =
        perlin.noise(u * period - variation.current, u * period) * amplitude
      ctx.lineTo(x * segment, point + height / 2)
    }
    ctx.lineTo(width, height / 2)
    ctx.lineTo(width, variant === 'top' ? height : 0)
    ctx.lineTo(0, variant === 'top' ? height : 0)
    ctx.closePath()
    ctx.fill()

    variation.current += speed
  }, [segments, amplitude, period, speed, variant, backgroundColor, perlin])

  const resize = () => {
    if (!canvas.current) return

    const { width, height } = canvas.current.getBoundingClientRect()
    canvas.current.width = width
    canvas.current.height = height
  }

  useEffect(() => resize(), [])
  useEvent('resize', resize)

  useEffect(() => {
    if (!canvas.current) return

    context.current = canvas.current?.getContext('2d')

    gsap.ticker.add(tick)

    return () => {
      gsap.ticker.remove(tick)
    }
  }, [tick])

  return isCorporate ? (
    <canvas
      ref={canvas}
      className={cn(
        'relative h-s-spacer-lg w-full md:h-d-spacer-lg',
        {
          '-bottom-[1px]': variant === 'top',
          '-top-[1px]': variant === 'bottom'
        },
        className
      )}
      {...props}
    />
  ) : null
}

export default FluidSpacer
